import { createSystem, defaultConfig, defineRecipe, defineSlotRecipe } from '@chakra-ui/react';
import {
  breadcrumbAnatomy,
  checkboxAnatomy,
  fieldAnatomy,
  radioGroupAnatomy,
  switchAnatomy,
  tabsAnatomy,
} from '@chakra-ui/react/anatomy';

const sansSerif = [
  // English
  '"Helvetica Neue"',
  'Arial',
  // Japanese
  '"Hiragino Kaku Gothic ProN"',
  '"Hiragino Sans"',
  'Meiryo',
  'sans-serif',
].join(',');

const monospace = [
  // English
  'SFMono-Regular',
  'Menlo',
  'Monaco',
  'Consolas',
  // Japanese
  '"Hiragino Kaku Gothic ProN"',
  '"Hiragino Sans"',
  'Meiryo',
  'monospace',
].join(',');

export const system = createSystem(defaultConfig, {
  globalCss: {
    body: {
      lineHeight: 1.75,
      touchAction: 'manipulation',
    },
    th: {
      textTransform: 'none',
    },
  },
  theme: {
    tokens: {
      colors: {
        // Tailwind CSS emerald
        brand: {
          50: { value: 'oklch(97.9% 0.021 166.113)' },
          100: { value: 'oklch(95% 0.052 163.051)' },
          200: { value: 'oklch(90.5% 0.093 164.15)' },
          300: { value: 'oklch(84.5% 0.143 164.978)' },
          400: { value: 'oklch(76.5% 0.177 163.223)' },
          500: { value: 'oklch(69.6% 0.17 162.48)' },
          600: { value: 'oklch(59.6% 0.145 163.225)' },
          700: { value: 'oklch(50.8% 0.118 165.612)' },
          800: { value: 'oklch(43.2% 0.095 166.913)' },
          900: { value: 'oklch(37.8% 0.077 168.94)' },
          950: { value: 'oklch(26.2% 0.051 172.552)' },
        },
      },
      fonts: {
        body: { value: sansSerif },
        heading: { value: sansSerif },
        mono: { value: monospace },
      },
      sizes: {
        // 「横幅最大」との差が体感できるように、親要素の幅の88%を上限としつつ、
        // 「横幅標準」（`container`レシピで指定している`5xl`）より狭くならないようにする。
        materialWide: { value: 'clamp({sizes.5xl}, 88%, 105rem)' },
      },
    },
    semanticTokens: {
      colors: {
        // ブランドカラーが緑系の色なので、統一感を出すために、`success`の緑色をブランドカラーで置き換える。
        bg: {
          success: { value: { _light: '{colors.brand.50}', _dark: '{colors.brand.950}' } },
        },
        border: {
          success: { value: { _light: '{colors.brand.500}', _dark: '{colors.brand.400}' } },
        },
        fg: {
          success: { value: { _light: '{colors.brand.600}', _dark: '{colors.brand.300}' } },
        },
        // デフォルトテーマと同じ命名規則のセマンティックトークンを、ブランドカラーに対しても定義する。
        brand: {
          contrast: { value: { _light: 'white', _dark: 'white' } },
          fg: { value: { _light: '{colors.brand.700}', _dark: '{colors.brand.300}' } },
          subtle: { value: { _light: '{colors.brand.100}', _dark: '{colors.brand.900}' } },
          muted: { value: { _light: '{colors.brand.200}', _dark: '{colors.brand.800}' } },
          emphasized: { value: { _light: '{colors.brand.300}', _dark: '{colors.brand.700}' } },
          solid: { value: { _light: '{colors.brand.600}', _dark: '{colors.brand.600}' } },
          focusRing: { value: { _light: '{colors.brand.500}', _dark: '{colors.brand.500}' } },
        },
      },
    },
    recipes: {
      button: defineRecipe({
        base: {
          fontWeight: 'bold',
        },
        variants: {
          size: {
            sm: { _icon: { boxSize: 5 } },
            md: { _icon: { boxSize: 6 } },
          },
        },
      }),
      container: defineRecipe({
        base: {
          maxW: '5xl',
        },
      }),
      heading: defineRecipe({
        base: {
          fontWeight: 'bold',
        },
      }),
      link: defineRecipe({
        base: {
          colorPalette: 'brand',
        },
      }),
      slider: defineRecipe({
        base: {
          colorPalette: 'brand',
        },
      }),
    },
    slotRecipes: {
      breadcrumb: defineSlotRecipe({
        slots: breadcrumbAnatomy.keys(),
        defaultVariants: {
          size: 'lg',
        },
      }),
      checkbox: defineSlotRecipe({
        slots: checkboxAnatomy.keys(),
        base: {
          root: {
            colorPalette: 'brand',
          },
        },
      }),
      field: defineSlotRecipe({
        slots: fieldAnatomy.keys(),
        base: {
          label: {
            fontWeight: 'bold',
          },
        },
      }),
      radioGroup: defineSlotRecipe({
        slots: radioGroupAnatomy.keys(),
        base: {
          root: {
            colorPalette: 'brand',
          },
        },
      }),
      switch: defineSlotRecipe({
        slots: switchAnatomy.keys(),
        base: {
          control: {
            colorPalette: 'brand',
          },
          label: {
            fontWeight: 'bold',
          },
        },
      }),
      tabs: defineSlotRecipe({
        slots: tabsAnatomy.keys(),
        base: {
          list: {
            colorPalette: 'brand',
          },
        },
      }),
    },
  },
});
